import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Session, SessionPriceTier, Patient } from '../../types/firestoreTypes';
import { Button } from '../ui';
import { SESSION_STATUS } from '../../types/constants';
import { Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

export interface SessionListProps {
  sessions: Session[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  isFetchingMore: boolean;
  selectedSessionIds: Set<string>;
  onSelectionChange: (sessionIds: Set<string>) => void;
  onSessionSave: (session: Session) => Promise<void>;
  priceTiers: SessionPriceTier[];
  defaultPriceTierId: string;
  patient: Patient;
}

export const SessionList: React.FC<SessionListProps> = ({
  sessions,
  isLoading,
  error,
  hasMore,
  onLoadMore,
  isFetchingMore,
  selectedSessionIds,
  onSelectionChange,
  onSessionSave,
  priceTiers,
  defaultPriceTierId,
  patient,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isSessionEligibleForInvoicing = (session: Session) => {
    return (
      session.status === SESSION_STATUS.COMPLETED &&
      !session.invoiceId
    );
  };

  const convertTimestampToDate = (timestamp: Timestamp) => {
    return timestamp.toDate();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case SESSION_STATUS.SCHEDULED:
        return 'bg-[#F0F9FF] text-[#026AA2]';
      case SESSION_STATUS.COMPLETED:
        return 'bg-[#ECFDF3] text-[#027A48]';
      case SESSION_STATUS.CANCELLED:
        return 'bg-[#FEF3F2] text-[#B42318]';
      case SESSION_STATUS.NO_SHOW:
        return 'bg-[#FFF6ED] text-[#B93815]';
      case SESSION_STATUS.INVOICED:
        return 'bg-[#F9F5FF] text-[#6941C6]';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      {sessions.map((session) => (
        <div
          key={session.id}
          className="bg-white rounded-lg border border-[#EAECF0] p-4 cursor-pointer hover:bg-gray-50"
          onClick={() => navigate(`/patients/${session.patientId}/sessions/${session.id}`)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectedSessionIds.has(session.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  const newSelectedIds = new Set(selectedSessionIds);
                  if (e.target.checked) {
                    if (isSessionEligibleForInvoicing(session)) {
                      newSelectedIds.add(session.id);
                    }
                  } else {
                    newSelectedIds.delete(session.id);
                  }
                  onSelectionChange(newSelectedIds);
                }}
                onClick={(e) => e.stopPropagation()}
                disabled={!isSessionEligibleForInvoicing(session)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t('patientSessionList.table.selectRowAriaLabel', {
                  date: session.sessionDateTime instanceof Timestamp
                    ? session.sessionDateTime.toDate().toLocaleDateString()
                    : t('patientSessionList.table.invalidDate'),
                })}
              />
              <div>
                <p className="font-medium text-[#101828]">
                  {format(convertTimestampToDate(session.sessionDateTime), 'PPP')}
                </p>
                <p className="text-sm text-gray-500">
                  {format(convertTimestampToDate(session.sessionDateTime), 'p')}
                </p>
                <div className="mt-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                  <span>
                    {t('sessionList.sessionTypeLabel')}: {t(`sessionType.${session.sessionType}`, session.sessionType)}
                  </span>
                  <span>
                    {t('sessionList.durationLabel')}: {t('sessionList.duration', { count: session.durationMinutes })}
                  </span>
                  <span>
                    {t('sessionList.priceLabel')}: {session.price.currency === 'HUF' 
                      ? Math.round(session.price.amount).toLocaleString() 
                      : session.price.amount.toFixed(2)} {session.price.currency}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                {t(`sessionStatus.${session.status.toLowerCase()}`)}
              </span>
              {session.invoiceId && (
                <span className="text-xs text-gray-400">
                  {t('sessionList.invoiced')} ID: {session.invoiceId}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}

      {hasMore && !isLoading && !isFetchingMore && (
        <div className="flex justify-center pt-4">
          <Button
            variant="secondary"
            onClick={onLoadMore}
            disabled={isFetchingMore}
          >
            {t('sessionList.buttons.loadMore')}
          </Button>
        </div>
      )}

      {isFetchingMore && (
        <div className="text-sm text-gray-500 text-center pt-4">
          {t('sessionList.status.loadingMore')}
        </div>
      )}

      {!hasMore && sessions.length > 0 && !isLoading && !isFetchingMore && (
        <p className="text-sm text-gray-500 text-center pt-4">
          {t('sessionList.status.endOfList')}
        </p>
      )}
    </div>
  );
};